import ErrorBoundary from "@/components/ErrorBoundary";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import UpdatePrompt from "@/components/UpdatePrompt";
import { AuthProvider } from "@/context/AuthContext";
import Login from "@/pages/Login";
import { setNavigateRef } from "@/utils/navigation";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Suspense, lazy } from "react";
import { BrowserRouter, Route, Routes, useNavigate } from "react-router";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Expenses = lazy(() => import("@/pages/Expenses"));
const ExpenseNew = lazy(() => import("@/pages/ExpenseNew"));
const ExpenseDetail = lazy(() => import("@/pages/ExpenseDetail"));
const ExpenseEdit = lazy(() => import("@/pages/ExpenseEdit"));
const Transfers = lazy(() => import("@/pages/Transfers"));
const TransferNew = lazy(() => import("@/pages/TransferNew"));
const TransferDetail = lazy(() => import("@/pages/TransferDetail"));
const TransferEdit = lazy(() => import("@/pages/TransferEdit"));
const Savings = lazy(() => import("@/pages/Savings"));
const SavingsNew = lazy(() => import("@/pages/SavingsNew"));
const SavingsDetail = lazy(() => import("@/pages/SavingsDetail"));
const SavingsEdit = lazy(() => import("@/pages/SavingsEdit"));
const Stats = lazy(() => import("@/pages/Stats"));
const Settings = lazy(() => import("@/pages/Settings"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function NavigationSetter() {
  setNavigateRef(useNavigate());
  return null;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <NavigationSetter />
        <UpdatePrompt />
        <AuthProvider>
          <ErrorBoundary>
            <Suspense>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route element={<ProtectedRoute />}>
                  <Route element={<Layout />}>
                    <Route index element={<Dashboard />} />
                    <Route path="expenses" element={<Expenses />} />
                    <Route path="expenses/new" element={<ExpenseNew />} />
                    <Route path="expenses/:id" element={<ExpenseDetail />} />
                    <Route path="expenses/:id/edit" element={<ExpenseEdit />} />
                    <Route path="transfers" element={<Transfers />} />
                    <Route path="transfers/new" element={<TransferNew />} />
                    <Route path="transfers/:id" element={<TransferDetail />} />
                    <Route path="transfers/:id/edit" element={<TransferEdit />} />
                    <Route path="savings" element={<Savings />} />
                    <Route path="savings/new" element={<SavingsNew />} />
                    <Route path="savings/:id" element={<SavingsDetail />} />
                    <Route path="savings/:id/edit" element={<SavingsEdit />} />
                    <Route path="stats" element={<Stats />} />
                    <Route path="settings" element={<Settings />} />
                  </Route>
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
